import type { EmojiData, EmojiDataResolver } from 'frimousse'

/*
  Die Emoji-Daten für frimousse — **aus dem App-Paket, nicht vom CDN**
  (entschieden 2026-09-25): Der Icon-Wähler muss offline gehen. Die JSON-
  Dateien von `emojibase-data` werden erst geladen, wenn der Emoji-Tab
  aufgeht (eigener Chunk). Die Umrechnung folgt frimousses eigenem
  `defaultEmojiDataResolver`.
*/

interface EmojibaseEmoji {
  emoji: string
  label: string
  group?: number
  subgroup?: number
  version: number
  tags?: string[]
  skins?: { emoji: string; tone?: number | number[] }[]
}

interface EmojibaseMessages {
  groups: { key: string; message: string; order: number }[]
  subgroups: { key: string; message: string; order: number }[]
  skinTones: { key: string; message: string }[]
}

type SkinToneKey = 'light' | 'medium-light' | 'medium' | 'medium-dark' | 'dark'
const TONES: readonly (SkinToneKey | 'none')[] = ['none', 'light', 'medium-light', 'medium', 'medium-dark', 'dark']

async function load(locale: 'de' | 'en'): Promise<{ emojis: EmojibaseEmoji[]; messages: EmojibaseMessages }> {
  if (locale === 'de') {
    const [emojis, messages] = await Promise.all([
      import('emojibase-data/de/data.json'),
      import('emojibase-data/de/messages.json'),
    ])
    return { emojis: emojis.default as EmojibaseEmoji[], messages: messages.default as EmojibaseMessages }
  }
  const [emojis, messages] = await Promise.all([
    import('emojibase-data/en/data.json'),
    import('emojibase-data/en/messages.json'),
  ])
  return { emojis: emojis.default as EmojibaseEmoji[], messages: messages.default as EmojibaseMessages }
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

export function toEmojiData(locale: string, emojis: EmojibaseEmoji[], messages: EmojibaseMessages): EmojiData {
  const flags = messages.subgroups.find(s => s.key === 'country-flag' || s.key === 'subdivision-flag')
  return {
    locale,
    categories: messages.groups
      .filter(g => g.key !== 'component')
      .map(g => ({ index: g.order, label: capitalize(g.message) })),
    skinTones: Object.fromEntries(
      messages.skinTones.map(t => [t.key, capitalize(t.message)]),
    ) as EmojiData['skinTones'],
    emojis: emojis
      .filter(e => e.group !== undefined)
      .map(e => {
        const skins = e.skins
          ?.filter(s => typeof s.tone === 'number')
          .reduce<Partial<Record<SkinToneKey, string>>>((acc, s) => {
            acc[TONES[s.tone as number] as SkinToneKey] = s.emoji
            return acc
          }, {})
        return {
          emoji: e.emoji,
          category: e.group as number,
          version: e.version,
          label: capitalize(e.label),
          tags: e.tags ?? [],
          countryFlag: (flags && e.subgroup === flags.order) || undefined,
          skins: skins && Object.keys(skins).length ? (skins as Record<SkinToneKey, string>) : undefined,
        }
      }),
  }
}

/**
 * Die höchste Emoji-Version, die die Systemschrift zeichnen kann — was
 * darüber liegt, bliebe ein leeres Kästchen (unter Windows oft Flaggen und
 * die neuesten Zeichen). Gemessen wird ein Zeichen je Version.
 */
function measureSupport(emojis: EmojiData['emojis']): { version: number; flags: boolean } {
  const all = { version: Infinity, flags: true }
  if (typeof document === 'undefined') return all
  let ctx: CanvasRenderingContext2D | null = null
  try {
    ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  } catch {
    return all
  }
  if (!ctx) return all
  const draws = (emoji: string) => {
    ctx!.canvas.width = ctx!.canvas.height = 2
    ctx!.font = "2px 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif"
    ctx!.textBaseline = 'middle'
    if (ctx!.measureText(emoji).width >= 4) return false
    ctx!.fillStyle = '#00f'
    ctx!.fillText(emoji, 0, 0)
    const blue = ctx!.getImageData(0, 0, 2, 2).data
    ctx!.clearRect(0, 0, 2, 2)
    ctx!.fillStyle = '#f00'
    ctx!.fillText(emoji, 0, 0)
    const red = ctx!.getImageData(0, 0, 2, 2).data
    // Ein farbiges Emoji ändert sich mit fillStyle nicht; ein Ersatzkästchen schon.
    for (let i = 0; i < blue.length; i += 4) {
      if (blue[i] !== red[i] || blue[i + 1] !== red[i + 1] || blue[i + 2] !== red[i + 2]) return false
    }
    return true
  }
  const firstPerVersion = new Map<number, string>()
  for (const e of emojis) if (!firstPerVersion.has(e.version)) firstPerVersion.set(e.version, e.emoji)
  const versions = [...firstPerVersion.keys()].sort((a, b) => b - a)
  // Windows zeichnet Länderflaggen als zwei Buchstaben.
  const flags = draws('\u{1F1EA}\u{1F1FA}')
  for (const v of versions) if (draws(firstPerVersion.get(v)!)) return { version: v, flags }
  return { version: versions[versions.length - 1] ?? 0, flags }
}

const cache = new Map<string, Promise<EmojiData>>()

export const resolveBundledEmojiData: EmojiDataResolver = locale => {
  const lang = locale.startsWith('de') ? 'de' : 'en'
  let pending = cache.get(lang)
  if (!pending) {
    pending = load(lang).then(({ emojis, messages }) => {
      const data = toEmojiData(lang, emojis, messages)
      const support = measureSupport(data.emojis)
      return {
        ...data,
        emojis: data.emojis.filter(e => e.version <= support.version && (support.flags || !e.countryFlag)),
      }
    })
    cache.set(lang, pending)
  }
  return pending
}

/** Kennung der Kategorie „Zuletzt benutzt“ — vor allen anderen (Emojibase zählt ab 0). */
export const RECENT_EMOJI_CATEGORY = -1

/**
 * Die Emoji-Daten mit **„Zuletzt benutzt“ als erster Kategorie** (gewünscht
 * 2026-09-26): So liegt die Zeile im selben Scrollbereich wie alle Emojis,
 * statt oben stehen zu bleiben. frimousse zeichnet die Liste virtuell und
 * rechnet Zeilen ab dem oberen Rand — eigener Inhalt über der Liste
 * verschöbe diese Rechnung; eine Kategorie tut es nicht.
 */
export function withRecentEmojis(base: EmojiDataResolver, recent: readonly string[], label: string): EmojiDataResolver {
  return async (locale, options) => {
    const data = await base(locale, options)
    if (!recent.length) return data
    const byEmoji = new Map<string, EmojiData['emojis'][number]>()
    for (const e of data.emojis) {
      byEmoji.set(e.emoji, e)
      for (const skin of Object.values(e.skins ?? {})) byEmoji.set(skin, e)
    }
    const picks = recent.flatMap(emoji => {
      const entry = byEmoji.get(emoji)
      return entry ? [{ ...entry, category: RECENT_EMOJI_CATEGORY }] : []
    })
    if (!picks.length) return data
    return {
      ...data,
      categories: [{ index: RECENT_EMOJI_CATEGORY, label }, ...data.categories],
      emojis: [...picks, ...data.emojis],
    }
  }
}
